"use client";

import Image from "next/image";
import { useSyncExternalStore } from "react";
import Link from "next/link";
import {
  BadgeCheck,
  Building2,
  ChevronRight,
  MapPin,
  Navigation,
  Package,
  Star,
} from "lucide-react";

import { getBusinessConfig } from "@/lib/business-config";
import { getStorefrontUrl } from "@/lib/storefront-url";
import type { CompanyBusinessType } from "@/types/company";

type SupplierCardProps = {
  name: string;
  location: string;
  slug: string;
  businessType: CompanyBusinessType;
  initials: string;
  logoUrl?: string | null;
  productsCount?: number;
  ratingAverage?: number | null;
  ratingCount?: number;
  isVerified?: boolean;
  distanceKm?: number | null;
  publicMode?: "presence" | "store";
  customSubdomainActive?: boolean;
  customDomainActive?: string | null;
  initialStorefrontUrl?: string;
};

const subscribeToHydration = () => () => {};
const clientSnapshot = () => true;
const serverSnapshot = () => false;

function getSegmentLabel(
  businessType: CompanyBusinessType,
) {
  switch (businessType) {
    case "retail":
      return "Comércio";
    case "wholesale":
      return "Atacado";
    case "food":
      return "Alimentação";
    case "services":
      return "Serviços";
    case "real_estate":
      return "Imobiliária";
    case "vehicles":
      return "Veículos";
    case "consortium_insurance":
      return "Consórcios e Seguros";
    default:
      return "Empresa";
  }
}

export default function SupplierCard({
  name,
  location,
  slug,
  businessType,
  initials,
  logoUrl,
  productsCount = 0,
  ratingAverage = null,
  ratingCount = 0,
  isVerified = false,
  distanceKm,
  publicMode = "store",
  customSubdomainActive = false,
  customDomainActive = null,
  initialStorefrontUrl,
}: SupplierCardProps) {
  const hydrated = useSyncExternalStore(subscribeToHydration, clientSnapshot, serverSnapshot);
  const businessConfig =
    getBusinessConfig(businessType);

  const segmentLabel =
    getSegmentLabel(businessType);

  const storefrontUrl = getStorefrontUrl({
    slug,
    custom_subdomain_active:
      customSubdomainActive,
    custom_domain_active:
      customDomainActive,
  });

  return (
    <Link
      href={!hydrated && initialStorefrontUrl ? initialStorefrontUrl : storefrontUrl}
      className="group relative block rounded-lg border border-slate-200 bg-white p-5 pr-10 transition-all duration-200 hover:-translate-y-0.5 hover:border-blue-400 hover:shadow-md focus-visible:outline-2 focus-visible:outline-blue-600"
    >
      <div className="flex items-start gap-3">
        <div className={`relative flex h-[72px] w-[72px] shrink-0 items-center justify-center overflow-hidden rounded-lg text-lg font-extrabold text-blue-600 ${logoUrl ? "" : "border border-blue-100 bg-blue-50"}`}>
          {logoUrl ? (
            <Image
              src={logoUrl}
              alt={"Logo da empresa " + name}
              fill
              sizes="72px"
              unoptimized
              className="object-contain"
            />
          ) : (
            initials
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-1.5">
            <h2 className="break-words text-base font-bold leading-6 text-slate-950">
              {name}
            </h2>

            {isVerified && (
              <BadgeCheck
                size={16}
                className="shrink-0 fill-blue-600 text-white"
                aria-label="Empresa verificada"
              />
            )}
          </div>

          <div className="mt-1.5 flex items-center gap-1.5 text-[11px] leading-4 text-slate-500">
            <MapPin
              size={13}
              className="shrink-0 text-slate-400"
            />

            <span className="truncate">
              {location}
            </span>
          </div>

          <div className="mt-1.5 flex items-center gap-1.5 text-[11px] leading-4">
            <Star
              size={13}
              className={
                ratingCount > 0
                  ? "shrink-0 fill-amber-400 text-amber-400"
                  : "shrink-0 text-slate-300"
              }
            />

            {ratingCount > 0 &&
            ratingAverage != null ? (
              <span className="font-semibold text-slate-700">
                {Number(
                  ratingAverage,
                ).toLocaleString(
                  "pt-BR",
                  {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  },
                )}{" "}
                <span className="font-medium text-slate-400">
                  ({ratingCount})
                </span>
              </span>
            ) : (
              <span className="font-medium text-slate-400">
                Novo
              </span>
            )}
          </div>

          {distanceKm != null && (
            <div className="mt-1.5 flex items-center gap-1.5 text-[11px] font-semibold leading-4 text-emerald-700">
              <Navigation
                size={13}
                className="shrink-0"
              />

              <span>
                {distanceKm.toLocaleString(
                  "pt-BR",
                  {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  },
                )}{" "}
                km de você
              </span>
            </div>
          )}

          <div className="mt-1.5 flex items-center gap-1.5 text-[11px] leading-4 text-slate-500">
            <Building2
              size={13}
              className="shrink-0 text-slate-400"
            />

            <span className="truncate">
              {segmentLabel}
            </span>
          </div>

          {publicMode === "store" && (
            <div className="mt-1.5 flex items-center gap-1.5 text-[11px] leading-4 text-slate-500">
              <Package
                size={13}
                className="shrink-0 text-slate-400"
              />

              <span className="truncate">
                {productsCount}{" "}
                {productsCount === 1
                  ? businessConfig.itemSingularLower +
                    " disponível"
                  : businessConfig.itemPluralLower +
                    " disponíveis"}
              </span>
            </div>
          )}

          {publicMode === "presence" && (
            <div className="mt-1.5 flex items-center gap-1.5 text-[11px] leading-4 text-slate-500">
              <Package
                size={13}
                className="shrink-0 text-slate-400"
              />

              <span>
                Perfil comercial
              </span>
            </div>
          )}

        </div>
      </div>

      <ChevronRight
        size={18}
        aria-hidden="true"
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-blue-600"
      />
    </Link>
  );
}
